/**
 * Echobot Messages Library
 * Static messages for quick responses without AI calls
 * Supports German (de) and English (en)
 */

import { EchobotAnimation } from '../types/echobot';

type SupportedLanguage = 'de' | 'en';

interface MessageConfig {
  content: string;
  animation: EchobotAnimation;
  actionButton?: {
    label: string;
    action: string;
  };
}

// Bilingual message structure
interface BilingualMessage {
  de: MessageConfig;
  en: MessageConfig;
}

export const echobotMessages = {
  // Welcome messages by context
  welcome: {
    dashboard: {
      content: 'Hallo! Ich bin Echobot, dein persönlicher Assistent! 🤖',
      animation: 'wave' as EchobotAnimation
    },
    onboarding: {
      content: 'Willkommen! Ich helfe dir beim Setup! 🚀',
      animation: 'wave' as EchobotAnimation
    },
    googleAds: {
      content: 'Hi! Ich bin Echo, dein Google Ads Assistent! 🎯',
      animation: 'wave' as EchobotAnimation
    },
    'google-ads': {
      content: 'Hi! Ich bin Echo, dein Google Ads Assistent! 🎯',
      animation: 'wave' as EchobotAnimation
    },
    tasks: {
      content: 'Hallo! Lass uns deine Tasks organisieren! ✅',
      animation: 'wave' as EchobotAnimation
    },
    opportunities: {
      content: 'Hey! Bereit für erfolgreiche Deals? 💼',
      animation: 'wave' as EchobotAnimation
    },
    leads: {
      content: 'Hallo! Lass uns deine Leads durchgehen! 📊',
      animation: 'wave' as EchobotAnimation
    },
    stream: {
      content: 'Hi! Hier findest du alle Aktivitäten! 📰',
      animation: 'wave' as EchobotAnimation
    },
    emails: {
      content: 'Hey! Ich helfe dir mit deinen E-Mails! 📧',
      animation: 'wave' as EchobotAnimation
    },
    settings: {
      content: 'Hi! Brauchst du Hilfe mit den Einstellungen? ⚙️',
      animation: 'wave' as EchobotAnimation
    },
    'time-tracking': {
      content: 'Hallo! Ich helfe dir mit der Zeiterfassung! ⏱️',
      animation: 'wave' as EchobotAnimation
    },
    companies: {
      content: 'Hi! Lass uns deine Firmen organisieren! 🏢',
      animation: 'wave' as EchobotAnimation
    },
    customers: {
      content: 'Hallo! Ich helfe dir mit deinen Kunden! 🤝',
      animation: 'wave' as EchobotAnimation
    },
    automate: {
      content: 'Hey! Bereit für MEDDPICC-Automatisierung? 🤖',
      animation: 'wave' as EchobotAnimation
    },
    'closing-assistant': {
      content: 'Hi! Lass uns Deals schließen! 💼',
      animation: 'wave' as EchobotAnimation
    },
    offers: {
      content: 'Hallo! Ich helfe dir Angebote zu erstellen! 📄',
      animation: 'wave' as EchobotAnimation
    }
  },

  // Onboarding-specific messages
  onboarding: {
    step1: {
      content: 'Gib deine Website ein, ich analysiere sie automatisch! 🔍',
      animation: 'question' as EchobotAnimation
    },
    analyzing: {
      content: 'Ich schaue mir deine Website an... ⏳',
      animation: 'question' as EchobotAnimation
    },
    analysisSuccess: {
      content: 'Perfekt! Ich habe alles erkannt! ✨',
      animation: 'celebrate' as EchobotAnimation
    },
    analysisError: {
      content: 'Hmm, da gab es ein Problem... 😕',
      animation: 'shake' as EchobotAnimation
    },
    industryConfirm: {
      content: 'Super Wahl! Das passt perfekt! 👍',
      animation: 'celebrate' as EchobotAnimation
    },
    almostDone: {
      content: 'Fast geschafft! Noch ein paar Details... 📝',
      animation: 'wave' as EchobotAnimation
    },
    modulesAdvice: {
      content: 'Diese Module passen zu deiner Branche! 🎁',
      animation: 'question' as EchobotAnimation
    },
    completed: {
      content: 'Geschafft! Willkommen bei Aideline! 🎉',
      animation: 'celebrate' as EchobotAnimation
    },
    skipWarning: {
      content: 'Bist du sicher? Das Setup dauert nur 2 Minuten! ⏱️',
      animation: 'question' as EchobotAnimation
    }
  },

  // Dashboard tips
  dashboardTips: [
    {
      content: 'Tipp: Nutze Quick Actions für schnellen Zugriff! ⚡',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Schau dir die Top 3 Prioritäten an! 🎯',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Der Morning Brief zeigt dir was wichtig ist! 📅',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Überprüfe deine Pipeline regelmäßig! 📊',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Vergiss nicht deine Tasks zu checken! ✅',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Google Ads tips
  googleAdsTips: [
    {
      content: 'Tipp: CTR unter 1%? Optimiere deine Keywords! 🎯',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Keywords prüfen',
        action: 'check-keywords'
      }
    },
    {
      content: 'Performance Max nutzt alle Google Kanäle! 🚀',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Teste verschiedene Anzeigentexte für bessere CTR! 📝',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Budget zu hoch? Schau dir das Keyword-Matching an! 💰',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Task tips
  taskTips: [
    {
      content: 'Priorisiere nach Deadline und Wichtigkeit! ⏰',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Erledige überfällige Tasks zuerst! 🔥',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Gruppiere ähnliche Tasks zusammen! 📦',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Tipp: Nutze Task-Gruppen für bessere Organisation! 🗂️',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Setze realistische Deadlines für bessere Planung! 📅',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Markiere wichtige Tasks mit High Priority! ⭐',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Lead tips
  leadTips: [
    {
      content: 'Qualifiziere Leads mit Lead-Scoring! 🎯',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Follow-up innerhalb 24h erhöht Conversion! ⏱️',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Dokumentiere alle Kontakte im Lead! 📝',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Nutze Lead-Sources für besseres Tracking! 📊',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Opportunity tips
  opportunityTips: [
    {
      content: 'MEDDPICC hilft beim Qualifizieren! 📋',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Was ist MEDDPICC?',
        action: 'explain-meddpicc'
      }
    },
    {
      content: 'Update regelmäßig den Deal-Status! 🔄',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Dokumentiere alle Kundeninteraktionen! 📝',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Proactive messages
  proactive: {
    noCampaigns: {
      content: 'Du hast noch keine Kampagnen. Soll ich helfen? 🚀',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Kampagne erstellen',
        action: 'navigate-to-google-ads'
      }
    },
    noLeads: {
      content: 'Noch keine Leads? Leg jetzt los! 📈',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Lead anlegen',
        action: 'open-add-lead-modal'
      }
    },
    lowPerformance: {
      content: 'Deine CTR könnte besser sein. Lass uns optimieren! 🎯',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Optimieren',
        action: 'start-optimization'
      }
    },
    overdueTasks: {
      content: 'Du hast überfällige Tasks. Zeit sie anzugehen! ⚠️',
      animation: 'shake' as EchobotAnimation,
      actionButton: {
        label: 'Tasks anzeigen',
        action: 'navigate-to-tasks'
      }
    },
    inactiveOpportunities: {
      content: 'Einige Deals sind inaktiv. Nachfassen? 📞',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Deals prüfen',
        action: 'navigate-to-opportunities'
      }
    },
    unassignedLeads: {
      content: 'Leads ohne Zuweisung! Wer kümmert sich? 👤',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Leads zuweisen',
        action: 'navigate-to-leads'
      }
    },
    goodMorning: {
      content: 'Guten Morgen! Bereit für einen produktiven Tag? ☀️',
      animation: 'wave' as EchobotAnimation
    },
    endOfDay: {
      content: 'Fast geschafft! Schließ noch offene Tasks ab! 🌙',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Tasks prüfen',
        action: 'navigate-to-tasks'
      }
    },
    weekend: {
      content: 'Schönes Wochenende! Montag geht\'s weiter! 🎉',
      animation: 'celebrate' as EchobotAnimation
    }
  },

  // Milestone celebrations
  milestones: {
    firstCampaign: {
      content: 'Glückwunsch zu deiner ersten Kampagne! 🎉',
      animation: 'celebrate' as EchobotAnimation
    },
    tenLeads: {
      content: 'Wow! 10 Leads erreicht! Weiter so! 🌟',
      animation: 'celebrate' as EchobotAnimation
    },
    firstDeal: {
      content: 'Dein erster Deal! Das ist großartig! 💰',
      animation: 'celebrate' as EchobotAnimation
    },
    hundredTasks: {
      content: '100 Tasks erledigt! Du bist fleißig! ✨',
      animation: 'celebrate' as EchobotAnimation
    }
  },

  // Error messages
  errors: {
    generic: {
      content: 'Ups, da lief etwas schief. Versuch es nochmal! 🔧',
      animation: 'shake' as EchobotAnimation
    },
    apiError: {
      content: 'Verbindungsproblem. Bitte warte kurz... 🌐',
      animation: 'shake' as EchobotAnimation
    },
    noPermission: {
      content: 'Dafür brauchst du mehr Rechte! 🔒',
      animation: 'shake' as EchobotAnimation
    }
  },

  // Success messages
  success: {
    campaignCreated: {
      content: 'Kampagne erfolgreich erstellt! 🚀',
      animation: 'celebrate' as EchobotAnimation
    },
    leadCreated: {
      content: 'Lead erfolgreich angelegt! 📝',
      animation: 'celebrate' as EchobotAnimation
    },
    taskCompleted: {
      content: 'Super! Task erledigt! ✅',
      animation: 'celebrate' as EchobotAnimation
    },
    dealClosed: {
      content: 'Deal gewonnen! Fantastisch! 🎊',
      animation: 'celebrate' as EchobotAnimation
    }
  },

  // Time Tracking tips
  timeTrackingTips: [
    {
      content: 'Tracke Zeit direkt bei Tasks für genaue Abrechnung! ⏱️',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Exportiere Zeiterfassung als PDF für Kunden! 📄',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Nutze Start/Stop-Timer für präzise Erfassung! ⏰',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Companies tips
  companiesTips: [
    {
      content: 'Verknüpfe Firmen mit Leads für besseres Tracking! 🔗',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Firmen-Daten helfen bei Opportunity-Bewertung! 💼',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Nutze Firmen-Notizen für Team-Knowledge! 📝',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Customers tips
  customersTips: [
    {
      content: 'Pflege Kundendaten für langfristige Beziehungen! 🤝',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Dokumentiere alle Kundeninteraktionen! 📋',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Cross-Selling-Chancen bei bestehenden Kunden! 💰',
      animation: 'question' as EchobotAnimation
    }
  ],

  // Automate/MEDDPICC tips
  automateTips: [
    {
      content: 'MEDDPICC hilft komplexe Deals zu qualifizieren! 🎯',
      animation: 'question' as EchobotAnimation,
      actionButton: {
        label: 'Was ist MEDDPICC?',
        action: 'explain-meddpicc'
      }
    },
    {
      content: 'Auto-Mode schlägt nächste Schritte vor! 🤖',
      animation: 'question' as EchobotAnimation
    },
    {
      content: 'Dokumentiere alle Entscheidungs-Kriterien! 📝',
      animation: 'question' as EchobotAnimation
    }
  ],

  // General tips (random)
  generalTips: [
    'Wusstest du? Du kannst Tasks per Drag & Drop verschieben! 🖱️',
    'Nutze die Suche für schnellen Zugriff! 🔍',
    'Keyboard-Shortcuts machen dich schneller! ⌨️',
    'Exportiere Daten für Berichte! 📊',
    'Lade dein Team ein für bessere Zusammenarbeit! 👥',
    'Setze Erinnerungen für wichtige Follow-ups! 🔔',
    'Nutze Tags für bessere Organisation! 🏷️',
    'Speichere häufige Filter als Favoriten! ⭐'
  ]
};

// Bilingual messages for key contexts
export const bilingualMessages: Record<string, Record<string, BilingualMessage>> = {
  welcome: {
    dashboard: {
      de: { content: 'Hallo! Ich bin Echobot, dein persönlicher Assistent! 🤖', animation: 'wave' },
      en: { content: 'Hello! I am Echobot, your personal assistant! 🤖', animation: 'wave' }
    },
    onboarding: {
      de: { content: 'Willkommen! Ich helfe dir beim Setup! 🚀', animation: 'wave' },
      en: { content: 'Welcome! I\'ll help you with the setup! 🚀', animation: 'wave' }
    },
    'google-ads': {
      de: { content: 'Hi! Ich bin Echo, dein Google Ads Assistent! 🎯', animation: 'wave' },
      en: { content: 'Hi! I am Echo, your Google Ads assistant! 🎯', animation: 'wave' }
    },
    tasks: {
      de: { content: 'Hallo! Lass uns deine Tasks organisieren! ✅', animation: 'wave' },
      en: { content: 'Hello! Let\'s organize your tasks! ✅', animation: 'wave' }
    },
    opportunities: {
      de: { content: 'Hey! Bereit für erfolgreiche Deals? 💼', animation: 'wave' },
      en: { content: 'Hey! Ready for successful deals? 💼', animation: 'wave' }
    },
    leads: {
      de: { content: 'Hallo! Lass uns deine Leads durchgehen! 📊', animation: 'wave' },
      en: { content: 'Hello! Let\'s go through your leads! 📊', animation: 'wave' }
    },
    stream: {
      de: { content: 'Hi! Hier findest du alle Aktivitäten! 📰', animation: 'wave' },
      en: { content: 'Hi! Here you\'ll find all activities! 📰', animation: 'wave' }
    },
    settings: {
      de: { content: 'Hi! Brauchst du Hilfe mit den Einstellungen? ⚙️', animation: 'wave' },
      en: { content: 'Hi! Need help with settings? ⚙️', animation: 'wave' }
    }
  }
};

// Bilingual tips for different contexts
export const bilingualTips: Record<string, BilingualMessage[]> = {
  dashboard: [
    {
      de: { content: 'Tipp: Nutze Quick Actions für schnellen Zugriff! ⚡', animation: 'question' },
      en: { content: 'Tip: Use Quick Actions for fast access! ⚡', animation: 'question' }
    },
    {
      de: { content: 'Schau dir die Top 3 Prioritäten an! 🎯', animation: 'question' },
      en: { content: 'Check out the Top 3 priorities! 🎯', animation: 'question' }
    },
    {
      de: { content: 'Der Morning Brief zeigt dir was wichtig ist! 📅', animation: 'question' },
      en: { content: 'The Morning Brief shows you what\'s important! 📅', animation: 'question' }
    },
    {
      de: { content: 'Überprüfe deine Pipeline regelmäßig! 📊', animation: 'question' },
      en: { content: 'Check your pipeline regularly! 📊', animation: 'question' }
    }
  ],
  tasks: [
    {
      de: { content: 'Priorisiere nach Deadline und Wichtigkeit! ⏰', animation: 'question' },
      en: { content: 'Prioritize by deadline and importance! ⏰', animation: 'question' }
    },
    {
      de: { content: 'Erledige überfällige Tasks zuerst! 🔥', animation: 'question' },
      en: { content: 'Complete overdue tasks first! 🔥', animation: 'question' }
    },
    {
      de: { content: 'Gruppiere ähnliche Tasks zusammen! 📦', animation: 'question' },
      en: { content: 'Group similar tasks together! 📦', animation: 'question' }
    }
  ],
  leads: [
    {
      de: { content: 'Qualifiziere Leads mit Lead-Scoring! 🎯', animation: 'question' },
      en: { content: 'Qualify leads with lead scoring! 🎯', animation: 'question' }
    },
    {
      de: { content: 'Follow-up innerhalb 24h erhöht Conversion! ⏱️', animation: 'question' },
      en: { content: 'Follow-up within 24h increases conversion! ⏱️', animation: 'question' }
    }
  ],
  opportunities: [
    {
      de: { content: 'MEDDPICC hilft beim Qualifizieren! 📋', animation: 'question', actionButton: { label: 'Was ist MEDDPICC?', action: 'explain-meddpicc' } },
      en: { content: 'MEDDPICC helps with qualification! 📋', animation: 'question', actionButton: { label: 'What is MEDDPICC?', action: 'explain-meddpicc' } }
    },
    {
      de: { content: 'Update regelmäßig den Deal-Status! 🔄', animation: 'question' },
      en: { content: 'Update deal status regularly! 🔄', animation: 'question' }
    }
  ],
  'google-ads': [
    {
      de: { content: 'Tipp: CTR unter 1%? Optimiere deine Keywords! 🎯', animation: 'question', actionButton: { label: 'Keywords prüfen', action: 'check-keywords' } },
      en: { content: 'Tip: CTR below 1%? Optimize your keywords! 🎯', animation: 'question', actionButton: { label: 'Check keywords', action: 'check-keywords' } }
    },
    {
      de: { content: 'Performance Max nutzt alle Google Kanäle! 🚀', animation: 'question' },
      en: { content: 'Performance Max uses all Google channels! 🚀', animation: 'question' }
    }
  ],
  general: [
    {
      de: { content: 'Wusstest du? Du kannst Tasks per Drag & Drop verschieben! 🖱️', animation: 'question' },
      en: { content: 'Did you know? You can move tasks with drag & drop! 🖱️', animation: 'question' }
    },
    {
      de: { content: 'Nutze die Suche für schnellen Zugriff! 🔍', animation: 'question' },
      en: { content: 'Use search for quick access! 🔍', animation: 'question' }
    },
    {
      de: { content: 'Keyboard-Shortcuts machen dich schneller! ⌨️', animation: 'question' },
      en: { content: 'Keyboard shortcuts make you faster! ⌨️', animation: 'question' }
    },
    {
      de: { content: 'Lade dein Team ein für bessere Zusammenarbeit! 👥', animation: 'question' },
      en: { content: 'Invite your team for better collaboration! 👥', animation: 'question' }
    }
  ]
};

/**
 * Get localized message for a specific category and key
 */
export function getLocalizedMessage(
  category: string, 
  key: string, 
  language: SupportedLanguage = 'de'
): MessageConfig | null {
  const categoryMessages = bilingualMessages[category];
  if (!categoryMessages) return null;
  
  const message = categoryMessages[key];
  if (!message) return null;
  
  return message[language] || message.de;
}

/**
 * Helper function to get random tip from array with language support
 */
export function getRandomTip(tips: MessageConfig[], language?: SupportedLanguage): MessageConfig {
  // If language is provided and we have bilingual tips, use them
  if (language) {
    // Check if the first tip has bilingual structure
    const contextKey = findContextKeyForTips(tips);
    if (contextKey && bilingualTips[contextKey]) {
      const bilingualTipsForContext = bilingualTips[contextKey];
      const randomBilingual = bilingualTipsForContext[Math.floor(Math.random() * bilingualTipsForContext.length)];
      return randomBilingual[language] || randomBilingual.de;
    }
  }
  
  // Fallback to original behavior
  return tips[Math.floor(Math.random() * tips.length)];
}

/**
 * Helper to find context key for a set of tips
 */
function findContextKeyForTips(tips: MessageConfig[]): string | null {
  if (tips === echobotMessages.dashboardTips) return 'dashboard';
  if (tips === echobotMessages.taskTips) return 'tasks';
  if (tips === echobotMessages.leadTips) return 'leads';
  if (tips === echobotMessages.opportunityTips) return 'opportunities';
  if (tips === echobotMessages.googleAdsTips) return 'google-ads';
  return null;
}

/**
 * Helper function to get random general tip with language support
 */
export function getRandomGeneralTip(language?: SupportedLanguage): MessageConfig {
  if (language && bilingualTips.general) {
    const tip = bilingualTips.general[Math.floor(Math.random() * bilingualTips.general.length)];
    return tip[language] || tip.de;
  }
  
  // Fallback to original behavior
  const tip = echobotMessages.generalTips[
    Math.floor(Math.random() * echobotMessages.generalTips.length)
  ];
  return {
    content: tip,
    animation: 'question' as EchobotAnimation
  };
}

